/**
 * @file landing.component.ts
 * @description Public marketing landing page for Kirigami.
 *
 * Composes the `<main>` marketing funnel:
 *   hero → quote → feature stage → encrypted → upload → why-Kirigami → divider → FAQs → contact.
 * Pricing now lives on the dedicated `/pricing` page, reached via the nav.
 *
 * This page is unguarded (no auth required). The header above `<main>`
 * switches between the minimal marketing nav and the authenticated
 * `<app-header>` based on `AuthService.isAuthenticated`.
 *
 * The page scrolls natively. Until 2026-11-03 a wide window with a real
 * pointer stepped it one section, and one feature screenshot, per gesture
 * (`docs/superpowers/specs/2026-09-22-landing-scroll-stepping-design.md`);
 * the owner dropped it: a visitor should never have to pass every screenshot.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { scrollIntoViewRespectingMotion } from '@kirigami/platform/browser';
import { of, throwError } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  isAllowedUploadExtension,
  isAllowedUploadSize,
  resolveTokenGate,
  type TokenGateReason,
  type BillingFallbackCopy,
  type BillingFallbackDecision,
} from '@kirigami/domain/analysis-rules';
import {
  ReportApiError,
  REPORT_SUBSCRIPTION_EXPIRED,
} from '@kirigami/data-access/report-adapters-web';
import { AuthService } from '../../services/auth.service';
import { BillingFallbackService } from '../../services/billing-fallback.service';
import { FileService } from '../../services/file.service';
import { ReportService } from '../../services/report.service';
import { ReportPollingService } from '../../services/report-polling.service';
import { AnalyticsService } from '../../services/analytics.service';
import { LoginGateDialogComponent } from '../shared/login-gate-dialog/login-gate-dialog.component';
import { SiteHeaderComponent } from '../shared/site-header/site-header.component';
import {
  UpgradeGateDialogComponent,
  SUBSCRIPTION_EXPIRED_GATE_TITLE,
  SUBSCRIPTION_EXPIRED_GATE_MESSAGE,
} from '../shared/upgrade-gate-dialog/upgrade-gate-dialog.component';
import { UploadConfirmDialogComponent } from '../shared/upload-confirm-dialog/upload-confirm-dialog.component';
import { BillingFallbackDialogComponent } from '../shared/billing-fallback-dialog/billing-fallback-dialog.component';
import { HeroWidgetComponent } from './widgets/hero-widget.component';
import { FeatureStageWidgetComponent } from './widgets/feature-stage-widget.component';
import { QuoteWidgetComponent } from './widgets/quote-widget.component';
import { EncryptedWidgetComponent } from './widgets/encrypted-widget.component';
import { UploadSectionWidgetComponent } from './widgets/upload-section-widget.component';
import { FeaturesWidgetComponent } from './widgets/features-widget.component';
import { FaqsWidgetComponent } from './widgets/faqs-widget.component';
import { ContactWidgetComponent } from './widgets/contact-widget.component';
import { ContactModalComponent } from './widgets/contact-modal.component';
import { SiteFooterComponent } from '../shared/site-footer/site-footer.component';
import { TranslatePipe } from '@kirigami/shared/i18n';
import { appPaths } from '../shared/app-paths';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    SiteHeaderComponent,
    HeroWidgetComponent,
    FeatureStageWidgetComponent,
    QuoteWidgetComponent,
    EncryptedWidgetComponent,
    UploadSectionWidgetComponent,
    FeaturesWidgetComponent,
    FaqsWidgetComponent,
    ContactWidgetComponent,
    ContactModalComponent,
    SiteFooterComponent,
    UploadConfirmDialogComponent,
    LoginGateDialogComponent,
    UpgradeGateDialogComponent,
    BillingFallbackDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'landing-page',
  },
})
export class LandingComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly reportService = inject(ReportService);
  private readonly fileService = inject(FileService);
  private readonly reportPolling = inject(ReportPollingService);
  private readonly analytics = inject(AnalyticsService);
  private readonly billingFallback = inject(BillingFallbackService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Global "an analysis is running" flag (owned by `ReportPollingService`).
   * Disables the dropzone widget and guards the upload pipeline so only one
   * analysis runs at a time across the app.
   */
  readonly analysisActive = this.reportPolling.analysisActive;

  /**
   * Reactive authentication flag — same `AuthService.isAuthenticated`
   * computed signal used elsewhere. Lets the landing swap the marketing
   * nav for the full app header when a valid session is in storage.
   */
  readonly isAuthenticated = this.authService.isAuthenticated;

  /** Holds the file selected by the upload widget while the confirm dialog is open. */
  readonly pendingFile = signal<File | null>(null);
  /** Derived signal — filename displayed inside the upload-confirm dialog. */
  readonly pendingFileName = computed(() => this.pendingFile()?.name ?? '');
  /** Controls the login-required modal shown when an anonymous visitor tries to upload. */
  readonly loginGateOpen = signal(false);
  /** Controls the credits-gate dialog (authenticated uploads only). */
  readonly creditsExhausted = signal(false);
  /** Plan-aware reason feeding the credits-gate dialog. */
  readonly tokenGateReason = signal<TokenGateReason | null>(null);
  /** Reset date surfaced by the credits-gate dialog. */
  readonly tokenGateResetDate = computed(() => this.authService.activeUsage().resetDate ?? null);
  /**
   * Custom gate copy for refusals with no `TokenGateReason` (today: the
   * backend's 403 SUBSCRIPTION_EXPIRED on start-analysis). Rendered only
   * while `tokenGateReason` is null — a reason's copy always wins.
   */
  readonly gateTitle = signal<string | null>(null);
  readonly gateMessage = signal<string | null>(null);

  /** Controls the in-page contact modal opened by the contact band's CTA. */
  readonly contactModalOpen = signal(false);
  /**
   * Billing-fallback confirmation state (active plan pool exhausted, other
   * money could pay): decision + copy drive the Continue/Cancel dialog while
   * the file waits for the user's answer.
   */
  readonly billingFallbackDecision = signal<BillingFallbackDecision | null>(null);
  readonly billingFallbackCopy = signal<BillingFallbackCopy | null>(null);
  #billingFallbackFile: File | null = null;

  /** Hero CTA → glide down to the dropzone. */
  onGetStarted(): void {
    const target = this.host.nativeElement.querySelector<HTMLElement>('#upload-now');
    if (!target) return;
    scrollIntoViewRespectingMotion(target, { block: 'start' });
  }

  /** Contact CTA → open the in-page contact modal. */
  onContactUs(): void {
    this.contactModalOpen.set(true);
  }

  /**
   * "Book a demo" CTA — routed to the support center for now. A dedicated
   * scheduling flow can replace this without touching the contact widget.
   */
  onBookDemo(): void {
    this.router.navigate([appPaths().support]);
  }

  /**
   * File drop from the landing dropzone.
   *
   * Runs synchronous extension/size validation against the shared domain
   * rule; on failure, logs a warning (no inline UI today — tracked as
   * separate tech-debt) and returns. Authenticated visitors are then gated
   * on their spendable wallet balance — an empty wallet opens the
   * plan-aware {@link UpgradeGateDialogComponent} instead of the upload
   * confirmation. Anonymous visitors have no wallet, so the credit gate is
   * skipped entirely and they fall through to the upload-confirm dialog;
   * the auth gate itself is deferred to {@link confirmUpload} so they still
   * see the formatting guidance before being asked to log in.
   */
  onSelectFile(file: File): void {
    // Single-analysis lock: the dropzone is disabled while a run is active,
    // but guard here too so no selection path can slip past.
    if (this.analysisActive()) {
      return;
    }
    if (!isAllowedUploadExtension(file.name) || !isAllowedUploadSize(file.size)) {
      console.warn('[LandingComponent] Upload blocked: invalid file');
      return;
    }
    // Credit gate applies to authenticated users only — anonymous visitors have
    // no wallet and are routed to /login by confirmUpload instead.
    if (this.isAuthenticated()) {
      // Gate on the ACTIVE plan's spendable balance in integer credits (C7 —
      // its pool + the personal wallet; the backend floor already nets out
      // any legacy holds) — mirrors the backend gate.
      const spendable = this.authService.activeUsage().creditsAvailable;
      const gate = resolveTokenGate({
        spendable,
        isFreePlan: this.authService.isFreePlan(),
        resetDate: this.authService.activeUsage().resetDate,
        // No-plan users are blocked outright (reason 'no-plan'), balance aside.
        hasNoPlan: this.authService.hasNoPlan(),
        // Previously-used Free benefit → 'free-used' (honest one-time copy).
        freePlanConsumed: this.authService.freePlanConsumed(),
      });
      if (!gate.allowed) {
        this.gateTitle.set(null);
        this.gateMessage.set(null);
        this.tokenGateReason.set(gate.reason ?? null);
        this.creditsExhausted.set(true);
        this.pendingFile.set(null);
        return;
      }
    }
    this.pendingFile.set(file);
  }

  /**
   * Confirm button on the upload-confirm dialog.
   * Anonymous → /login with explicit `returnUrl=/dashboard` so post-login lands the user
   * back inside the app where they can re-upload. Authenticated → run the upload
   * pipeline; it navigates to /dashboard on success.
   */
  confirmUpload(): void {
    const file = this.pendingFile();
    this.pendingFile.set(null);
    if (!file) return;
    if (!this.isAuthenticated()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: appPaths().home } });
      return;
    }
    // Exhausted-plan check (per-plan pools): when the active plan's own pool
    // is empty but another subscription or the personal wallet could pay,
    // the user decides via the billing-fallback dialog before anything runs.
    const decision = this.billingFallback.resolve();
    if (decision.kind !== 'none') {
      this.#billingFallbackFile = file;
      this.billingFallbackDecision.set(decision);
      this.billingFallbackCopy.set(this.billingFallback.copyFor(decision));
      return;
    }
    this.runUploadPipeline(file);
  }

  /** Cancel / Esc / backdrop on the upload-confirm dialog. */
  cancelUpload(): void {
    this.pendingFile.set(null);
  }

  /**
   * Continue on the billing-fallback dialog: applies the decision (a switch
   * activates the funded subscription and announces it; the wallet variant
   * only confirms the spend) and starts the upload pipeline, which reads the
   * possibly-new `currentSubscription` at start time.
   */
  confirmBillingFallback(): void {
    const decision = this.billingFallbackDecision();
    const file = this.#billingFallbackFile;
    this.dismissBillingFallback();
    if (!decision || !file) return;
    this.billingFallback.confirm(decision);
    this.runUploadPipeline(file);
  }

  /** Cancel / Esc / backdrop on the billing-fallback dialog — nothing starts. */
  dismissBillingFallback(): void {
    this.#billingFallbackFile = null;
    this.billingFallbackDecision.set(null);
    this.billingFallbackCopy.set(null);
  }

  /** Anonymous visitor attempted to upload (click / keyboard / drop on the dropzone). */
  onLoginRequired(): void {
    this.loginGateOpen.set(true);
  }

  /** "Log in" CTA on the login-required modal → /login (returnUrl=/dashboard). */
  goToLogin(): void {
    this.loginGateOpen.set(false);
    this.router.navigate(['/login'], { queryParams: { returnUrl: appPaths().home } });
  }

  /** Cancel / Esc / backdrop on the login-required modal. */
  dismissLoginGate(): void {
    this.loginGateOpen.set(false);
  }

  /** Upgrade/Top up CTA on the credits-gate dialog → /pricing. */
  goToUpgrade(): void {
    this.creditsExhausted.set(false);
    this.router.navigate(['/pricing']);
  }

  /** Cancel / Esc / backdrop on the credits-gate dialog. */
  dismissGate(): void {
    this.creditsExhausted.set(false);
  }

  /** Runs the full upload pipeline for an already-validated, authed file. */
  private runUploadPipeline(file: File): void {
    // Final start-analysis guard — never start a second run while one is active.
    if (this.analysisActive()) {
      return;
    }

    // 'pay-as-you-go' is RETIRED as a billing/report scope (2026-07-31, backend
    // answers 403 PAY_AS_YOU_GO_RETIRED): never guess a billing id. If the plan
    // disappeared between the gate (file selection) and this confirm — e.g. a
    // profile refresh after plan expiry/cancellation — fail closed by re-opening
    // the plan gate instead of creating anything.
    const subscriptionId = this.authService.user()?.currentSubscription;
    if (!subscriptionId) {
      this.gateTitle.set(null);
      this.gateMessage.set(null);
      this.tokenGateReason.set('no-plan');
      this.creditsExhausted.set(true);
      return;
    }

    // Hold the global lock across the whole pipeline (create → upload →
    // start-analysis). On success hand off to polling (track) then release so
    // the lock survives the navigation to /dashboard; on error release so a
    // failed run never wedges the block.
    const releaseLock = this.reportPolling.beginAnalysisHold();

    const initialTitle = file.name.replace(/\.(pdf|docx|doc|tex)$/i, '');
    const owner = this.authService.user()?.email || '';

    this.reportService
      .createReport(initialTitle, '', file.name, owner, subscriptionId)
      .pipe(
        switchMap((report) => {
          const reportId = report.id;
          if (!reportId) {
            return throwError(
              () => new Error('Report was created but the server did not return an ID.'),
            );
          }
          return of({ ...report, id: reportId });
        }),
        switchMap((report) =>
          this.fileService
            .getUploadUrl(report.id, file.name, subscriptionId)
            .pipe(map((presigned) => ({ report, presigned }))),
        ),
        switchMap(({ report, presigned }) =>
          this.fileService.uploadFile(presigned, file).pipe(
            tap(() => {
              // `split('.').pop()` returns the whole filename for an
              // extensionless file (pop() never yields undefined there),
              // which would leak it into analytics as `file_type` — only
              // treat a dot as an extension marker when it's not the first
              // character (a leading dot is a dotfile, not an extension).
              const dot = file.name.lastIndexOf('.');
              const fileType = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : 'unknown';
              this.analytics.track({
                event: 'document_upload',
                source: 'landing',
                file_type: fileType,
              });
            }),
            map(() => report),
          ),
        ),
        switchMap((report) =>
          this.reportService.startAnalysis(report.id, subscriptionId).pipe(map(() => report)),
        ),
        takeUntilDestroyed(this.destroyRef),
        // Release on completion, error, OR teardown (navigation away) so a
        // failed/abandoned run never wedges the lock. Idempotent — the track()
        // in `next` still owns the lock on the happy path.
        finalize(() => releaseLock()),
      )
      .subscribe({
        next: (report) => {
          // Track BEFORE the stream completes so the tracked poll owns the lock
          // as ownership passes from the pipeline hold, keeping it held across
          // the navigation to /dashboard.
          this.reportPolling.track(report.id);
          this.authService.loadUserProfile().subscribe({
            next: () => undefined,
            error: () => undefined,
          });
          this.router.navigate([appPaths().home]);
        },
        error: (err) => {
          console.error('[LandingComponent] Upload failed:', err);
          // A 403 SUBSCRIPTION_EXPIRED refusal on start-analysis opens the
          // upgrade gate with the expired-plan copy (CTA → /pricing) and
          // resyncs the profile — /users/get now filters expired plans out
          // of allowed_subscriptions, so the stale plan disappears from
          // menus/meter without a manual reload. Refresh is best-effort.
          if (err instanceof ReportApiError && err.code === REPORT_SUBSCRIPTION_EXPIRED) {
            this.tokenGateReason.set(null);
            this.gateTitle.set(SUBSCRIPTION_EXPIRED_GATE_TITLE);
            this.gateMessage.set(SUBSCRIPTION_EXPIRED_GATE_MESSAGE);
            this.creditsExhausted.set(true);
            this.authService.loadUserProfile().subscribe({
              next: () => undefined,
              error: () => undefined,
            });
          }
        },
      });
  }
}
